import React, { memo } from 'react';
import Benefit from './Benefit';
import Container from '../Shared/Container';
import { Legal } from '../Shared/components';
import { BenefitsContainer, BenefitsContent, HeaderText } from './components';
import {
  eventParamAttributes,
  EventParamTypes,
} from '@spotify-internal/touchdown-analytics';
import { purify } from '../../lib/purify';
import { useImpressionTracking } from '../../lib/tracking/useImpressionTracking';

const noop = () => {};

type BenefitsPropTypes = {
  header?: { text: string };
  benefits?: React.ComponentProps<typeof Benefit>[];
  verticalBenefits?: boolean;
  legalDisclaimers?: string[];
  container?: React.ComponentProps<typeof Container>;
  size?: React.ComponentProps<typeof Benefit>['size'];
  eventParams?: EventParamTypes;
  onBenefitImpression?: (benefit: string, position: number) => void;
};

export function Benefits({
  header,
  benefits = [],
  verticalBenefits,
  legalDisclaimers = [],
  container,
  size,
  eventParams,
  onBenefitImpression,
}: BenefitsPropTypes) {
  const observe = useImpressionTracking(
    onBenefitImpression
      ? (key: string) => {
          const position = parseInt(key, 10);
          onBenefitImpression('benefit', position);
        }
      : noop,
  );

  return (
    <BenefitsContainer
      {...container}
      {...eventParamAttributes({ ...eventParams, componentType: 'promo' })}
    >
      {header && (
        <HeaderText
          variant="titleLarge"
          dangerouslySetInnerHTML={{
            __html: purify(header.text),
          }}
        />
      )}
      <BenefitsContent verticalBenefits={verticalBenefits || false}>
        {benefits.map((benefit, i) => (
          <Benefit
            key={`benefit-${i}`}
            ref={onBenefitImpression ? observe(String(i)) : undefined}
            {...benefit}
            size={size}
          />
        ))}
      </BenefitsContent>
      {legalDisclaimers.map((legalDisclaimer, i) => (
        <Legal key={`legal-${i}`} text={legalDisclaimer} />
      ))}
    </BenefitsContainer>
  );
}

export default memo(Benefits);
